import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { isResidentNotificationsMockEnabled } from './is-resident-notifications-mock';
import {
  getMockNotificationDetail,
  getMockNotificationList,
  markMockNotificationRead,
  replyToMockNotification,
} from './mocks';
import { normalizeNotificationFiles } from './map-notification';

type ApiSuccessResponse<T> = {
  status: 'success';
  data: T;
};

type ApiMessageResponse = {
  status: 'success';
  msg: string;
};

type ApiErrorResponse = {
  status: 'error';
  msg: string;
};

export type NotificationListItem = {
  id: number;
  createdAt: string;
  subject: string;
  status: string;
  text: string;
  managerReply?: boolean;
  files?: unknown[];
  replies?: unknown[];
};

export type NotificationListNav = {
  totalCount: number;
  pageSize: number;
  currentPage: number;
  pageCount: number;
};

export type NotificationListData = {
  messages: NotificationListItem[];
  countNewMessages: number;
  nav: NotificationListNav;
};

export type NotificationDetailData = {
  id: number;
  createdAt: string;
  subject: string;
  message: string;
  status: string;
  files: unknown[];
  from: string;
  to: string;
};

export const NOTIFICATIONS_PAGE_PARAM = 'nav-notifications';
export const RESIDENT_LK_NOTIFICATIONS_TAB_ID = 2;

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Сервер вернул некорректный ответ');
  }
}

function assertSuccessResponse<T>(
  response: Response,
  result: ApiSuccessResponse<T> | ApiErrorResponse,
  fallbackMessage: string,
): T {
  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : fallbackMessage,
    );
  }

  return (result as ApiSuccessResponse<T>).data;
}

function assertSuccessMessage(
  response: Response,
  result: ApiMessageResponse | ApiErrorResponse,
  fallbackMessage: string,
): string {
  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : fallbackMessage,
    );
  }

  return result.msg;
}

function requireSessid(): string {
  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  return sessid;
}

export function buildNotificationListUrl(params: { q?: string; page?: number }): string {
  const url = new URL(getApiUrl('/api/personal/notification/'), window.location.origin);

  const query = params.q?.trim();
  if (query) {
    url.searchParams.set('q', query);
  }

  if (params.page && params.page > 1) {
    url.searchParams.set(NOTIFICATIONS_PAGE_PARAM, `page-${params.page}`);
  }

  return url.toString();
}

export async function fetchNotificationList(params: {
  q?: string;
  page?: number;
}): Promise<NotificationListData> {
  if (isResidentNotificationsMockEnabled()) {
    return getMockNotificationList(params);
  }

  const response = await fetch(buildNotificationListUrl(params), {
    method: 'GET',
    credentials: 'same-origin',
  });

  const result = await parseJsonResponse<ApiSuccessResponse<NotificationListData> | ApiErrorResponse>(
    response,
  );

  const data = assertSuccessResponse(response, result, 'Не удалось загрузить список уведомлений');

  return {
    ...data,
    messages: data.messages.map((message) => ({
      ...message,
      files: normalizeNotificationFiles(message.files),
    })),
  };
}

export async function fetchNotificationDetail(id: number): Promise<NotificationDetailData> {
  if (isResidentNotificationsMockEnabled()) {
    return getMockNotificationDetail(id);
  }

  const response = await fetch(getApiUrl(`/api/personal/notification/${id}/`), {
    method: 'GET',
    credentials: 'same-origin',
  });

  const result = await parseJsonResponse<
    ApiSuccessResponse<NotificationDetailData> | ApiErrorResponse
  >(response);

  const data = assertSuccessResponse(response, result, 'Не удалось загрузить уведомление');

  return {
    ...data,
    files: normalizeNotificationFiles(data.files),
  };
}

export async function markNotificationRead(id: number): Promise<string> {
  if (isResidentNotificationsMockEnabled()) {
    return markMockNotificationRead(id);
  }

  const sessid = requireSessid();
  const body = JSON.stringify({ sessid });

  const response = await fetch(getApiUrl(`/api/personal/notification/${id}/`), {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Bitrix-Csrf-Token': sessid,
    },
    body,
  });

  const result = await parseJsonResponse<ApiMessageResponse | ApiErrorResponse>(response);
  return assertSuccessMessage(response, result, 'Не удалось пометить уведомление прочитанным');
}

export async function replyToNotification(
  id: number,
  payload: { subject: string; message: string },
): Promise<string> {
  if (isResidentNotificationsMockEnabled()) {
    return replyToMockNotification();
  }

  const sessid = requireSessid();
  const body = new FormData();

  body.append('parentId', String(id));
  body.append('subject', payload.subject);
  body.append('message', payload.message);
  body.append('sessid', sessid);

  const response = await fetch(getApiUrl(`/api/personal/notification/${id}/`), {
    method: 'POST',
    body,
    credentials: 'same-origin',
    headers: {
      'X-Bitrix-Csrf-Token': sessid,
    },
  });

  const result = await parseJsonResponse<ApiMessageResponse | ApiErrorResponse>(response);
  return assertSuccessMessage(response, result, 'Не удалось отправить ответ');
}
